import Image from 'next/image';
import { FC } from 'react';
import classNames from 'classnames';
import { Link, PrismicImageWithAlt } from '@/components';
import { getSpecialURL } from '@/utils';

import { PromoCarouselDocumentDataCarouselItemsItemWithID } from '@/types';

import {
  getPromoCardMediaState,
  getPromoContentAlignStyle,
  getPromoSpecialPresentation,
} from './utils';
import {
  PromoCardCta,
  PromoCardTitleDescriptionBlock,
} from './PromoCard.component';
import style from './promocard.module.scss';

interface IPromoCard {
  className?: string;
  specialUrl?: string;
  special?: PromoCarouselDocumentDataCarouselItemsItemWithID;
}

const PromoCard: FC<IPromoCard> = ({ className, specialUrl, special }) => {
  if (!special) return null;

  const pres = getPromoSpecialPresentation(special);
  const media = getPromoCardMediaState(special, pres);
  const contentAlignStyle = getPromoContentAlignStyle(special);
  const hideContent = pres.pictureOnly || pres.displayImageOnly;
  const hasContentBackground = Boolean(special.background_color);
  const resolvedSpecialUrl = specialUrl || getSpecialURL(special);
  const cardRootStyle = media.contentFullWidth
    ? media.cardBackgroundStyle
    : undefined;

  const hasCustomCtaLink = Boolean(special.cta_link);

  const cardLink =
    resolvedSpecialUrl && hasCustomCtaLink ? (
      <Link
        aria-label={`See details of ${pres.titleText || 'promotion'}`}
        className={style.card_hit_area}
        href={resolvedSpecialUrl}
      >
        <span className="sr-only">
          See details of {pres.titleText || 'promotion'}
        </span>
      </Link>
    ) : null;

  const renderCardContent = () => {
    if (special.definedInPrismic) {
      return (
        <>
          {cardLink}
          <figure
            className={classNames(style.picture, {
              [style.picture_small]: !hideContent,
              [style.picture_small_split]: !hideContent && hasContentBackground,
            })}
          >
            {media.showPrismicImage && (
              <PrismicImageWithAlt
                defaultAlt={pres.altText}
                field={special.picture}
                fill
              />
            )}
          </figure>
          {!hideContent && (
            <div
              className={style.content}
              style={{ ...pres.cardBackgroundStyle, ...contentAlignStyle }}
            >
              <PromoCardTitleDescriptionBlock
                descriptionWrapClassName={style.description}
                extraDescriptionClassName={style.extra_description}
                pres={pres}
              />
              <PromoCardCta
                ctaClassName={style.cta_button}
                ctaHref={special.cta_link}
                ctaOutlanderClassName={style.cta_button_outlander}
                pres={pres}
              />
            </div>
          )}
        </>
      );
    }

    return (
      <>
        {cardLink}
        {(media.showTreezImage || media.showPrismicImage) && (
          <figure
            className={classNames(style.picture, {
              [style.picture_small]: !hideContent && !media.contentFullWidth,
              [style.picture_small_split]:
                !hideContent && !media.contentFullWidth && hasContentBackground,
            })}
          >
            {media.showTreezImage ? (
              <Image alt={pres.altText} fill src={special.imageUrl!} />
            ) : (
              <PrismicImageWithAlt
                defaultAlt={pres.altText}
                field={special.picture}
                fill
              />
            )}
          </figure>
        )}
        {!hideContent && (
          <div
            className={classNames(style.content, {
              [style.content_full]: media.contentFullWidth,
            })}
            style={
              media.contentFullWidth
                ? contentAlignStyle
                : { ...pres.cardBackgroundStyle, ...contentAlignStyle }
            }
          >
            <PromoCardTitleDescriptionBlock
              descriptionWrapClassName={style.description}
              extraDescriptionClassName={style.extra_description}
              pres={pres}
            />
            <PromoCardCta
              ctaClassName={style.cta_button}
              ctaHref={special.cta_link}
              ctaOutlanderClassName={style.cta_button_outlander}
              pres={pres}
            />
          </div>
        )}
      </>
    );
  };

  if (!hasCustomCtaLink && resolvedSpecialUrl) {
    return (
      <Link
        aria-label={pres.cardAriaLabel}
        className={classNames(style.promotion_card, className)}
        href={resolvedSpecialUrl}
        style={cardRootStyle}
      >
        {renderCardContent()}
      </Link>
    );
  }

  return (
    <div
      aria-label={pres.cardAriaLabel}
      className={classNames(style.promotion_card, className)}
      role="group"
      style={cardRootStyle}
    >
      {renderCardContent()}
    </div>
  );
};

export { PromoCard };
